import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { MARKETS } from '../config.ts';
import { usePoll } from '../hooks/usePoll.ts';
import { candles as fetchCandles, HL_INTERVALS, hlEnabled, INTERVAL_MS, type Candle, type Timeframe } from '../lib/hl.ts';
import type { Tick } from '../lib/ticks.ts';

/** A price line of an open position: its entry (colored by side) or its bust price */
/** A price line: a position's entry or bust price, or a resting limit order */
export type Level = { price: number; label: string; kind: 'entry' | 'bust' | 'order'; isLong: boolean };

/** Seconds of price shown by the tick line */
const TICK_WINDOW_MS = 120_000;
const AXIS_W = 72;
const AXIS_H = 22;

type CandleTf = Exclude<Timeframe, 'tick'>;

/** Ticks bucketed into candles (seconds timeframes, and every timeframe on a local chain) */
function bucket(ticks: Tick[], ms: number): Candle[] {
  const out: Candle[] = [];
  for (const k of ticks) {
    const t = Math.floor(k.t / ms) * ms;
    const last = out[out.length - 1];
    if (last && last.t === t) {
      last.h = Math.max(last.h, k.px);
      last.l = Math.min(last.l, k.px);
      last.c = k.px;
    } else out.push({ t, o: last?.c ?? k.px, h: Math.max(k.px, last?.c ?? k.px), l: Math.min(k.px, last?.c ?? k.px), c: k.px });
  }
  return out;
}

/** At most two points (min and max) per pixel column, so a dense tick series stays cheap to draw */
function decimate(ticks: Tick[], t0: number, t1: number, cols: number): Tick[] {
  if (ticks.length <= cols * 2) return ticks;
  const out: Tick[] = [];
  const w = (t1 - t0) / cols;
  let col = -1;
  let lo: Tick | undefined;
  let hi: Tick | undefined;
  const flush = () => {
    if (!lo || !hi) return;
    if (lo === hi) out.push(lo);
    else out.push(...(lo.t < hi.t ? [lo, hi] : [hi, lo]));
  };
  for (const k of ticks) {
    // the tick kept from before the window lands in column -1, the initial `col`: clamp it into the first column
    const c = Math.max(0, Math.floor((k.t - t0) / w));
    if (c !== col) {
      flush();
      col = c;
      lo = hi = k;
    } else {
      if (k.px < lo!.px) lo = k;
      if (k.px > hi!.px) hi = k;
    }
  }
  flush();
  return out;
}

function niceStep(range: number, target: number): number {
  const raw = range / target;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  return (n < 1.5 ? 1 : n < 3 ? 2 : n < 7 ? 5 : 10) * mag;
}

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export function Chart({
  market,
  timeframe,
  ticks,
  version,
  live,
  scale,
  levels,
}: {
  market: number;
  timeframe: Timeframe;
  ticks: Tick[];
  /** Changes with every new tick (the array itself is reused) */
  version: number;
  live?: number;
  scale: number;
  levels: Level[];
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState<{ x: number; y: number }>();
  // price-axis zoom: the auto-fitted range times `yZoom` (1 = auto), around its center
  const [yZoom, setYZoom] = useState(1);
  const drag = useRef<{ y: number; zoom: number } | undefined>(undefined);
  // the tick line scrolls with the clock even between ticks
  const [now, setNow] = useState(Date.now());
  const isTick = timeframe === 'tick';
  const remoteCandles = hlEnabled && !isTick && HL_INTERVALS.includes(timeframe as CandleTf);
  const coin = MARKETS[market]?.hlCoin ?? 'BTC';
  const remote = usePoll(remoteCandles ? () => fetchCandles(coin, timeframe as CandleTf) : null, 15_000, [coin, timeframe]);

  useEffect(() => setYZoom(1), [market, timeframe]);
  useEffect(() => {
    if (!isTick) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [isTick]);

  const candles = useMemo(() => {
    if (isTick) return [];
    if (!remoteCandles) return bucket(ticks, INTERVAL_MS[timeframe as CandleTf]);
    const base = (remote.data ?? []).map((c) => ({ ...c }));
    // the live mid moves the last candle between fetches
    const last = base[base.length - 1];
    if (last && live) {
      last.c = live;
      last.h = Math.max(last.h, live);
      last.l = Math.min(last.l, live);
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTick, remoteCandles, ticks, version, timeframe, remote.data, live]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ w: Math.floor(e!.contentRect.width), h: Math.floor(e!.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geo = useMemo(() => {
    const pw = Math.max(0, size.w - AXIS_W);
    const ph = Math.max(0, size.h - AXIS_H);
    let prices: number[];
    let line: Tick[] = [];
    let visible: Candle[] = [];
    let slot = 8;
    const t1 = Math.max(now, ticks[ticks.length - 1]?.t ?? 0);
    const t0 = t1 - TICK_WINDOW_MS;
    if (isTick) {
      // keep the last tick before the window so the line enters from the left edge
      const first = ticks.findIndex((k) => k.t >= t0);
      const inWindow = first === -1 ? ticks.slice(-1) : ticks.slice(Math.max(0, first - 1));
      line = decimate(inWindow, t0, t1, Math.max(1, Math.floor(pw)));
      prices = line.map((k) => k.px);
    } else {
      // candles widen to fill the pane when there are few of them, and narrow down to 6 px before scrolling off
      visible = candles.slice(-Math.max(10, Math.floor(pw / 6)));
      slot = Math.min(14, Math.max(6, pw / Math.max(visible.length, 1)));
      prices = visible.flatMap((c) => [c.h, c.l]);
    }
    // levels stay off the scale: a far bust price would flatten the series; they are pinned to the edge instead
    if (prices.length === 0) for (const l of levels) prices.push(l.price);
    let lo = Math.min(...prices);
    let hi = Math.max(...prices);
    if (!isFinite(lo)) [lo, hi] = [0, 1];
    if (hi - lo < hi * 1e-5) [lo, hi] = [lo * 0.99995, hi * 1.00005];
    const pad = (hi - lo) * 0.08;
    lo -= pad;
    hi += pad;
    if (yZoom !== 1) {
      const mid = (lo + hi) / 2;
      const half = ((hi - lo) / 2) * yZoom;
      lo = mid - half;
      hi = mid + half;
    }
    const xc = (i: number) => pw - (visible.length - i - 0.5) * slot;
    const xt = (t: number) => ((t - t0) / (t1 - t0)) * pw;
    const y = (p: number) => ((hi - p) / (hi - lo)) * ph;
    return { pw, ph, slot, visible, line, lo, hi, xc, xt, y, t0, t1 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles, ticks, version, isTick, now, size, levels, yZoom]);

  useEffect(() => {
    const c = canvas.current;
    if (!c || size.w === 0) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = size.w * dpr;
    c.height = size.h * dpr;
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, size.w, size.h);
    const ink = css('--ink');
    const muted = css('--ink-muted');
    const grid = css('--grid');
    const pos = css('--pos');
    const neg = css('--neg');
    const { pw, ph, slot, visible, line, lo, hi, xc, xt, y, t0, t1 } = geo;
    const dp = scale >= 100 ? 2 : scale >= 10 ? 1 : 0;
    const fmt = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
    g.font = '11px Inter, system-ui, sans-serif';

    // grid and price axis
    g.textBaseline = 'middle';
    const step = niceStep(hi - lo, 6);
    // a calm market zooms below a cent: the labels take as many decimals as the step needs, up to the
    // oracle's own precision, or neighbours would read the same
    const axisDp = Number.isFinite(step) ? Math.min(Math.max(dp, Math.ceil(-Math.log10(step) - 1e-9)), Math.round(Math.log10(scale))) : dp;
    const axisFmt = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: axisDp, maximumFractionDigits: axisDp });
    for (let p = Math.ceil(lo / step) * step; p <= hi; p += step) {
      g.strokeStyle = grid;
      g.beginPath();
      g.moveTo(0, Math.round(y(p)) + 0.5);
      g.lineTo(pw, Math.round(y(p)) + 0.5);
      g.stroke();
      g.fillStyle = muted;
      g.fillText(axisFmt(p), pw + 8, y(p));
    }

    // time axis, to the second for the tick line and the seconds timeframes
    g.textBaseline = 'top';
    g.fillStyle = muted;
    if (isTick) {
      // the smallest round step that leaves room for a label (about 70 px) on narrow screens too
      const pxPerMs = pw / (t1 - t0);
      const stepMs = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000].find((st) => st * pxPerMs >= 70) ?? 300_000;
      g.textAlign = 'center';
      for (let t = Math.ceil(t0 / stepMs) * stepMs; t <= t1; t += stepMs) {
        const label = new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const lx = xt(t);
        // a label cut by the edges reads as noise
        if (lx < 26 || lx > pw - 26) continue;
        g.fillText(label, lx, ph + 6);
      }
      g.textAlign = 'left';
    } else {
      const ms = INTERVAL_MS[timeframe as CandleTf];
      const every = Math.max(1, Math.round(110 / slot));
      visible.forEach((k, i) => {
        if ((visible.length - 1 - i) % every !== 0) return;
        const d = new Date(k.t);
        const label =
          ms >= 86_400_000
            ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
            : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: ms < 60_000 ? '2-digit' : undefined });
        g.fillText(label, xc(i) - 14, ph + 6);
      });
    }

    // series and level lines stay inside the plot area, whatever the zoom
    g.save();
    g.beginPath();
    g.rect(0, 0, pw, ph);
    g.clip();
    if (isTick && line.length) {
      const pts: [number, number][] = line.map((k) => [xt(k.t), y(k.px)]);
      // the line holds its last price up to now
      pts.push([xt(t1), pts[pts.length - 1]![1]]);
      const area = g.createLinearGradient(0, 0, 0, ph);
      area.addColorStop(0, css('--area-top'));
      area.addColorStop(1, css('--area-bottom'));
      g.beginPath();
      g.moveTo(pts[0]![0], ph);
      for (const [px, py] of pts) g.lineTo(px, py);
      g.lineTo(pts[pts.length - 1]![0], ph);
      g.closePath();
      g.fillStyle = area;
      g.fill();
      g.beginPath();
      pts.forEach(([px, py], i) => (i ? g.lineTo(px, py) : g.moveTo(px, py)));
      g.lineJoin = 'round';
      g.lineCap = 'round';
      g.strokeStyle = css('--glow');
      g.lineWidth = 8;
      g.stroke();
      g.strokeStyle = css('--accent');
      g.lineWidth = 2;
      g.stroke();
    } else {
      const body = Math.max(1, slot - 3);
      g.lineWidth = 1;
      visible.forEach((k, i) => {
        const up = k.c >= k.o;
        g.strokeStyle = g.fillStyle = up ? pos : neg;
        const cx = Math.round(xc(i)) + 0.5;
        g.beginPath();
        g.moveTo(cx, y(k.h));
        g.lineTo(cx, y(k.l));
        g.stroke();
        const top = y(Math.max(k.o, k.c));
        g.fillRect(cx - body / 2, top, body, Math.max(1, y(Math.min(k.o, k.c)) - top));
      });
    }
    // position lines: entries solid in the side's color, bust prices dashed red, limit orders dotted
    g.lineWidth = 1;
    for (const l of levels) {
      if (l.price > hi || l.price < lo) continue;
      const ly = Math.round(y(l.price)) + 0.5;
      g.setLineDash(l.kind === 'bust' ? [5, 4] : l.kind === 'order' ? [2, 3] : []);
      g.strokeStyle = l.kind === 'bust' ? neg : l.isLong ? pos : neg;
      g.globalAlpha = l.kind === 'bust' ? 0.85 : 0.7;
      g.beginPath();
      g.moveTo(0, ly);
      g.lineTo(pw, ly);
      g.stroke();
    }
    g.globalAlpha = 1;
    g.setLineDash([]);
    g.restore();

    // a pill per level on the left (pinned to the top or bottom edge with an arrow when off the scale), its price
    // tagged on the axis; pills shift to stay apart when positions sit close together
    const onInk = css('--on-ink');
    const panel = css('--panel');
    const taken: number[] = [];
    const place = (want: number) => {
      let at = Math.min(ph - 10, Math.max(10, want));
      const dir = at < ph / 2 ? 1 : -1;
      for (let i = 0; i < 30 && taken.some((t) => Math.abs(t - at) < 19); i++) at += dir * 19;
      taken.push(at);
      return at;
    };
    const pill = (text: string, x: number, cy: number, color: string, filled: boolean) => {
      const w = g.measureText(text).width + 12;
      g.fillStyle = filled ? color : panel;
      g.strokeStyle = color;
      g.beginPath();
      g.roundRect(x, cy - 8, w, 16, 3);
      g.fill();
      if (!filled) g.stroke();
      g.fillStyle = filled ? onInk : color;
      g.textBaseline = 'middle';
      g.fillText(text, x + 6, cy + 0.5);
    };
    g.font = '600 10.5px Inter, system-ui, sans-serif';
    for (const l of [...levels].sort((a, b) => b.price - a.price)) {
      const color = l.kind === 'bust' ? neg : l.isLong ? pos : neg;
      const filled = l.kind === 'entry';
      const inRange = l.price <= hi && l.price >= lo;
      const arrow = inRange ? '' : l.price > hi ? '↑ ' : '↓ ';
      pill(`${arrow}${l.label}${inRange ? '' : ` ${fmt(l.price)}`}`, 6, place(inRange ? y(l.price) : l.price > hi ? 10 : ph - 10), color, filled);
      if (inRange) {
        const ly = y(l.price);
        g.fillStyle = filled ? color : panel;
        g.strokeStyle = color;
        g.beginPath();
        g.rect(pw + 0.5, ly - 8.5, size.w - pw - 1, 17);
        g.fill();
        if (!filled) g.stroke();
        g.fillStyle = filled ? onInk : color;
        g.textBaseline = 'middle';
        g.fillText(fmt(l.price), pw + 8, ly);
      }
    }
    g.font = '11px Inter, system-ui, sans-serif';

    // latest price: a dot at the head of the tick line, a tag on the axis (pinned to its ends when zoomed past)
    const lastPx = isTick ? line[line.length - 1]?.px : visible[visible.length - 1]?.c;
    if (lastPx !== undefined) {
      const rising = isTick ? lastPx >= (line[line.length - 2]?.px ?? lastPx) : lastPx >= visible[visible.length - 1]!.o;
      if (isTick && lastPx <= hi && lastPx >= lo) {
        g.fillStyle = ink;
        g.beginPath();
        g.arc(xt(t1), y(lastPx), 3.5, 0, Math.PI * 2);
        g.fill();
      }
      const ly = Math.min(ph - 9, Math.max(9, y(lastPx)));
      g.fillStyle = rising ? pos : neg;
      g.fillRect(pw, ly - 9, size.w - pw, 18);
      g.fillStyle = css('--on-ink');
      g.textBaseline = 'middle';
      g.fillText(fmt(lastPx), pw + 8, ly);
    }

    // crosshair
    if (hover && hover.x < pw && hover.y < ph) {
      g.strokeStyle = muted;
      g.setLineDash([2, 3]);
      g.beginPath();
      g.moveTo(hover.x + 0.5, 0);
      g.lineTo(hover.x + 0.5, ph);
      g.moveTo(0, hover.y + 0.5);
      g.lineTo(pw, hover.y + 0.5);
      g.stroke();
      g.setLineDash([]);
      const p = hi - (hover.y / ph) * (hi - lo);
      g.fillStyle = ink;
      g.fillRect(pw, hover.y - 9, size.w - pw, 18);
      g.fillStyle = css('--on-ink');
      g.textBaseline = 'middle';
      g.fillText(fmt(p), pw + 8, hover.y);
    }
  }, [geo, size, hover, levels, timeframe, isTick, scale]);

  const dp = scale >= 100 ? 2 : scale >= 10 ? 1 : 0;
  const f = (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  const onAxis = (x: number, y: number) => x >= geo.pw && y < geo.ph;

  let readout: ReactNode = null;
  if (hover && hover.x < geo.pw && hover.y < geo.ph) {
    if (isTick) {
      const t = geo.t0 + (hover.x / geo.pw) * (geo.t1 - geo.t0);
      let k: Tick | undefined;
      for (const x of geo.line) if (x.t <= t) k = x;
      if (k)
        readout = (
          <>
            {new Date(k.t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })} <b>{f(k.px)}</b>
          </>
        );
    } else {
      const c = geo.visible[Math.round((hover.x - geo.pw) / geo.slot + geo.visible.length - 0.5)];
      if (c)
        readout = (
          <>
            O <b>{f(c.o)}</b> H <b>{f(c.h)}</b> L <b>{f(c.l)}</b> C <b>{f(c.c)}</b>
          </>
        );
    }
  }
  const empty = isTick ? ticks.length === 0 : candles.length === 0;

  return (
    <div className="chart" ref={wrap}>
      {/* the cat watermark, centred on the plot area (the price and time axes excluded), under the canvas */}
      <div className="chart-mark" aria-hidden style={{ right: AXIS_W, bottom: AXIS_H }}>
        <i />
      </div>
      <canvas
        ref={canvas}
        style={{ width: size.w, height: size.h, cursor: drag.current || (hover && onAxis(hover.x, hover.y)) ? 'ns-resize' : 'crosshair' }}
        onPointerDown={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (e.button !== 0 || !onAxis(e.clientX - r.left, e.clientY - r.top)) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { y: e.clientY, zoom: yZoom };
          setHover(undefined);
        }}
        onPointerMove={(e) => {
          if (drag.current) {
            // dragging down widens the range (zoom out), up narrows it (zoom in); x2 every 150 px
            const z = drag.current.zoom * 2 ** ((e.clientY - drag.current.y) / 150);
            setYZoom(Math.min(20, Math.max(0.05, z)));
            return;
          }
          const r = e.currentTarget.getBoundingClientRect();
          setHover({ x: e.clientX - r.left, y: e.clientY - r.top });
        }}
        onPointerUp={(e) => {
          if (!drag.current) return;
          drag.current = undefined;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onDoubleClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          if (onAxis(e.clientX - r.left, e.clientY - r.top)) setYZoom(1);
        }}
        onPointerLeave={() => !drag.current && setHover(undefined)}
      />
      {yZoom !== 1 && (
        <button className="chart-auto" onClick={() => setYZoom(1)} title="Back to the automatic price scale (or double-click the axis)">
          Auto
        </button>
      )}
      {readout && <div className="chart-ohlc">{readout}</div>}
      {empty && <div className="chart-empty">{remote.error ? `Chart unavailable: ${remote.error}` : 'Waking the cat… waiting for prices'}</div>}
    </div>
  );
}
